/* Program Name: Login.js
 **  Description: Login Component
 **
 **
 ** Project    Date        Who  Comments
 ** ---------  ----------  ---  ---------------------------------------------------
 ** IS0200241  2021-03-17  STT  Initial program
 *********************************************************************************/

/**React import */
import React, { useState, useRef } from 'react'

/** MUI imports */ // mui.com/components
import {
  Button,
  Card,
  FormControlLabel,
  Checkbox,
  CardContent,
  TextField,
} from '@mui/material'

/** Custom Component imports */
import { PrimaryButtonFull } from '../ActionColorButtons'
import Footer from '../SideBar/Footer'
import { FormSelect } from '../CustomInputs'

/** Context imports */
import { useSession } from '../../../Context/SessionContext'
import { useAlert } from '../../../Context/AlertContext'
import { useDomain } from '../../../Context/DomainContext'

/** Style imports */
import { FormTextField } from '../CustomInputs'

/** API import */
import { authenticate } from '../../../Context/SessionAPI'
import { forgotPassword, getLoginDomains } from '../../../API/generalAPI'

/** validation imports */
import { confirmErrors, requiredField } from '../../../API/validationFunctions'
import { useLoading } from '../../../Context/LoadingContext'
import LoadingModal from '../Loading/LoadingModal'

/**Login **********************************************************************/
const Login = () => {
  /****************************************************************************/
  /**Local Variables */

  //errorMessage  - hooks into a function that triggers an error alert with given message
  //postAjaxAlert - hooks into a funciton that checks the ajax response and triggers appropriate alert
  const { errorMessage, postAjaxAlert } = useAlert()

  const loading = useRef(useLoading())

  const alert = useRef(useAlert())

  const { login } = useSession()

  const { strippedDomain } = useDomain()

  const [loginInfo, setLoginInfo] = useState({
    username: '',
    password: '',
    rememberMe: false,
    customer: '',
    domain: '',
  })

  const [inputErrors, setInputErrors] = useState({})

  const [domains, setDomains] = useState([])

  const [selectedDomain, setSelectedDomain] = useState('')

  const [forgotpassword, setForgotpassword] = useState(false)

  const usernameRef = useRef('')

  const [error, setError] = useState('')

  const [isLoading, setIsLoading] = useState(false)

  const handlePasswordReset = () => {
    setIsLoading(true)
    forgotPassword(usernameRef.current.value, strippedDomain)
      .then((response) => {
        if (response.data.ttStdRes[0].lSuccess) {
          setError('')
        } else {
          setError(response.data.ttStdRes[0].cErrorMsg)
        }
      })
      .catch((error) => {})
      .then(() => {
        setIsLoading(false)
      })
  }

  const styles = {
    link: {
      cursor: 'pointer',
      color: 'blue',
    },
    center: {
      textAlign: 'center',
    },
  }

  /****************************************************************************/
  /**Local Functions */

  /** Handle Text =>
   * @param input the name of the field that is being updated
   * handles the input for all String textfields in on this page
   */
  const handleText = (input) => (e) => {
    setLoginInfo({ ...loginInfo, [input]: e.target.value })
  }

  const handleLogin = (e) => {
    e.preventDefault()
    if (validate()) {
      authenticate({
        login: [
          {
            ...loginInfo,
            domain: selectedDomain ? selectedDomain : strippedDomain,
            customer: strippedDomain,
          },
        ],
      })
        .then((response) => {
          if (!response.data.meta[0].err) {
            login(response)
          } else {
            alert.current.errorMessage(response.data.meta[0].errmsg)
          }
          //postAjaxAlert(response.data.meta[0])
        })
        .catch((error) => {
          errorMessage(error.message)
        })
    }
  }

  const handleForgotPassword = () => {
    setForgotpassword(true)
  }

  /** this function handles the validation for the Add/Update User forms
   * this creates a temp object that holds any errors were made in the form
   * after checking for the error stati we update the inputErrors object
   * and then loop through the temp object and check if all errors were false
   * if all errors are false than validate will return true
   * if there are any errors false is returned */
  const validate = () => {
    var tempError = {}
    tempError.username = requiredField(loginInfo.username)
    tempError.password = requiredField(loginInfo.password)
    setInputErrors({ ...tempError })

    return confirmErrors(tempError)
  }

  const fetchDomains = () => {
    loading.current.startLoading()
    getLoginDomains(strippedDomain)
      .then((response) => {
        alert.current.postAjaxAlert(response.data.meta[0], 'rdr')
        if (!response.data.meta[0].err) {
          if (response.data.ttDomainRegistry) {
            var domains = []
            response.data.ttDomainRegistry.forEach((domain) => {
              domains.push({ value: domain.domainName, label: domain.desc })
            })
            setDomains(domains)
          } else {
            setDomains([])
          }
        }
      })
      .catch((error) => {
        alert.current.errorMessage(error.message)
      })
      .then(() => {
        loading.current.stopLoading()
      })
  }

  const handleDomain = (selectData) => {
    setSelectedDomain(selectData)
  }

  /****************************************************************************/
  /**Rendering */

  return (
    <div>
      <Card
        sx={{
          position: 'fixed',
          top: '40%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 500,
          borderRadius: 0,
        }}
        elevation={5}
      >
        <CardContent>
          {!forgotpassword ? (
            <div>
              <div style={styles.center}>
                <h1>Welcome to ezPOP</h1>
                <p>Sign in to your account</p>
              </div>
              <form onSubmit={handleLogin}>
                <div style={{ margin: 4 }}>
                  <div>
                    <FormTextField
                      fullWidth
                      value={loginInfo.username}
                      fieldName={`username`}
                      label={'Username'}
                      handleText={handleText}
                      inputErrors={inputErrors}
                    />
                  </div>
                  <div>
                    <FormTextField
                      fullWidth
                      password
                      type="password"
                      value={loginInfo.password}
                      fieldName={`password`}
                      label={'Password'}
                      handleText={handleText}
                      inputErrors={inputErrors}
                    />
                  </div>
                  {domains.length > 0 && (
                    <div>
                      <FormSelect
                        width={'100%'}
                        options={domains}
                        placeholder={'Select a Domain...'}
                        value={selectedDomain}
                        label={'Domain'}
                        handleSelect={handleDomain}
                        inputErrors={inputErrors}
                      />
                    </div>
                  )}
                  <div style={styles.center}>
                    {domains.length === 0 ? (
                      <p style={styles.link} onClick={fetchDomains}>
                        Show Domains
                      </p>
                    ) : (
                      <p
                        style={styles.link}
                        onClick={() => {
                          setDomains([])
                          setSelectedDomain('')
                        }}
                      >
                        Hide Domains
                      </p>
                    )}
                  </div>
                  {/* <div>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={loginInfo.rememberMe}
                        name="rememberMe"
                      />
                    }
                    label={
                      <p className={globalClasses.xsSize}>
                        Remember me on this computer
                      </p>
                    }
                  />
                </div> */}
                </div>
                <div>
                  <PrimaryButtonFull type="submit">Sign In</PrimaryButtonFull>
                </div>
              </form>
              <div>
                <Button
                  fullWidth
                  variant={'outlined'}
                  color={'primary'}
                  onClick={handleForgotPassword}
                >
                  Forgot Password
                </Button>
              </div>
              <div style={styles.center}>
                <a style={styles.link} href={'/scripts/serigraph'}>
                  Serigraph User
                </a>
              </div>
            </div>
          ) : (
            <div>
              <div style={styles.center}>
                <h1>Forgot Password</h1>
                <p style={{ fontSize: '12px' }}>
                  Enter your username in the field below and click the "reset"
                  button so that a new temporary password can be emailed to you.
                </p>
              </div>
              <p style={{ color: 'Red' }}>{error}</p>
              <div style={{ margin: 4 }}>
                <TextField
                  inputRef={usernameRef}
                  label={'Username'}
                  fullWidth
                />
              </div>
              <div style={{ margin: '16px 4px 64px 4px' }}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handlePasswordReset}
                >
                  Reset Password
                </Button>
              </div>
              <div style={styles.center}>
                <Button
                  onClick={() => {
                    setForgotpassword(false)
                  }}
                >
                  Back To Login
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      {isLoading && <LoadingModal message={'Please Wait While We Email You'} />}
      <Footer />
    </div>
  )
}

export default Login
